import {
  Button, Input,
  RecordInterface, SetFormValues,
} from '@components/common/Forms';
import { MirrorData, SetMirrorData } from '@type/job-add';
import { Tooltip, TailwindSpinner } from '@components/common';
import { PencilIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { useJobDomain } from '@components/templates/job-add/edit/hooks/useJobDomain';
import { dataTestId } from '@services/data-test-id';
import { JobDomainSuggest } from './partials/JobDomainSuggest';

/** Props */
interface JobDomainProps {
  locale: Locale,
  record: RecordInterface,
  setFormValues: SetFormValues,
  apiOccupation?: string,
  apiDomain?: string,
  apiDomainId?: string,
  shouldFetchDomain?: boolean,
  reqMirrorData?: Pick<MirrorData, 'title' | 'description'>,
  setMirrorData?: SetMirrorData,
  labelDescriptionClass?: string,
}

/**
 * @description Job Domain component. To be used in a form.
 * - `locale` - The current app locale.
 * - `record` - record function from useBjForm
 * - `setFormValues` - function to set the form values
 * - `apiOccupation` - the occupation name from the API
 * - `apiDomain` - the domain name from the API
 * - `apiDomainId` - the domain ID from the API
 * - `shouldFetchDomain` - whether to fetch the domain
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData` - function to update the mirror form state
 * - `labelDescriptionClass` - additional classes for the label description
 */
export const JobDomain = (props: JobDomainProps) => {
  // Destructure props
  const {
    locale,
    record,
    setFormValues,
    apiDomain,
    apiOccupation,
    apiDomainId,
    shouldFetchDomain,
    reqMirrorData,
    setMirrorData,
    labelDescriptionClass,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Job domain hook
  // **************************
  const {
    domainName,
    occupationName,
    isJobDomainLoading,
    showDomainSuggest,
    setShowDomainSuggest,
    setAutocompleteParentDomain,
    setAutocompleteOccupation,
    setHasSelectedDomain,
  } = useJobDomain({
    locale,
    setFormValues,
    apiOccupation,
    apiDomain,
    apiDomainId,
    shouldFetchDomain,
    reqMirrorData,
    setMirrorData,
  });

  // If occupationName is not provided, don't render the component
  if (!occupationName) return null;

  // Render
  // **************************
  return (
    <div className="border-y border-dashed border-input py-4">
      <div className="flex items-center justify-between gap-4 md:px-3.5">
        {/* Job domain */}
        <div className={`grow${isJobDomainLoading ? ' opacity-50' : ''}`}>
          <div className="text-sm font-bold text-ink-medium">
            {t('job.add.assigned_domain_label')}
          </div>
          <div className="first-letter:uppercase">
            <strong>{occupationName}</strong>
            {(domainName) && (
              <>
                {` ${t('global.ro_din_en_from')} `}
                <strong>{domainName}</strong>
              </>
            )}
          </div>
        </div>

        {/* Toggle domain suggest */}
        {!isJobDomainLoading && (
          <Tooltip
            position="top"
            colorScheme="black"
            tooltipText={t('job.add.autocomplete_assigned_domain_edit')}
          >
            <Button
              color="primary-light"
              styling="soft"
              size="sm"
              rounding="full"
              className="shrink-0"
              title={t('job.add.autocomplete_assigned_domain_edit')}
              onClick={() => {
                setShowDomainSuggest(!showDomainSuggest);
              }}
              {...dataTestId('jobAddPage.jobDomain.openEditButton')}
            >
              <PencilIcon className="size-4" />
            </Button>
          </Tooltip>
        )}

        {/* Loading spinner */}
        {isJobDomainLoading && (
          <TailwindSpinner className="size-6 text-secondary" />
        )}

      </div>

      {/* Hidden domain input */}
      <Input
        type="hidden"
        {...record('domain', {
          defaultValue: undefined,
        })}
      />

      {/* Domain suggest */}
      {showDomainSuggest && (
        <JobDomainSuggest
          record={record}
          setFormValues={setFormValues}
          labelDescriptionClass={labelDescriptionClass}
          occupationSetter={setAutocompleteOccupation}
          parentDomainSetter={setAutocompleteParentDomain}
          showDomainSuggestSetter={setShowDomainSuggest}
          setHasSelectedDomain={setHasSelectedDomain}
        />
      )}
    </div>
  );
};
